import { action, computed, makeObservable, observable, reaction } from 'mobx'
import type React from 'react'
import { createRef } from 'react'

import type AppStore from '@src/app/AppStore'
import type IUiStore from '@src/app/IUiStore'
import {
  AddNumberRow,
  ContactRow,
  getTypeLabel,
} from '@src/component/phone-number-selector/PhoneNumberSelectorController'
import type {
  PhoneNumberSelection,
  IdentityRow,
} from '@src/component/phone-number-selector/PhoneNumberSelectorController'
import { isNationalNumber } from '@src/lib/phone-number'
import type { ActiveCall, ActiveCallParticipant } from '@src/service/voice'

import VoicePhoneNumberSelectorController from './VoicePhoneNumberSelectorController'

type CallTransferMode = 'selector' | 'transfer'
export type CallTransferState = 'in-progress' | 'success' | 'failed'

export default class CallTransferController
  extends VoicePhoneNumberSelectorController
  implements IUiStore
{
  override readonly mode = 'single'
  override readonly lang = {
    SELECT_CUSTOM: 'Transfer to',
    PLACEHOLDER: 'Members, inboxes, contacts, or numbers...',
  }

  transferMode: CallTransferMode = 'selector'
  transferState: CallTransferState | null = null
  transfereeParticipantId: string | null = null

  readonly messageRef: React.MutableRefObject<HTMLInputElement | null> = createRef()

  message = ''
  messageFocused = false
  fromPhoneNumberId: string | null = null

  constructor(
    app: AppStore,
    override call: ActiveCall,
  ) {
    super(app, call)
    makeObservable(this, {
      maxMessageLengthReached: computed,
      selection: computed,
      transferee: computed,
      transferMode: observable.ref,
      transferState: observable.ref,
      transfereeParticipantId: observable.ref,
      message: observable.ref,
      messageFocused: observable.ref,
      handleBack: action.bound,
      handleMessageBlur: action.bound,
      handleMessageChange: action.bound,
      handleMessageFocus: action.bound,
      handleTransfer: action.bound,
      handleLeaveCall: action.bound,
    })

    this.disposeBag.add(
      reaction(
        () => this.call.participants.list,
        (participants) => {
          if (
            (this.transferState === 'in-progress' || this.transferState === 'failed') &&
            this.transfereeParticipantId
          ) {
            const participant = participants.find(
              (p) => p.id === this.transfereeParticipantId,
            )

            if (participant) {
              switch (participant.status) {
                case 'active':
                case 'completed':
                  this.transferState = 'success'
                  this.transfereeParticipantId = null
                  this.fromPhoneNumberId = null
                  break
                case 'busy':
                case 'failed':
                case 'no-answer':
                  this.transferState = 'failed'
                  break
                case 'ringing':
                  this.transferState = 'in-progress'
              }
            }
          }
        },
        { name: 'CallParticipantsChanged' },
      ),
      reaction(
        () => this.transferState,
        () => {
          this.debug(
            'change in transfer state (transferState: %O) (transfereeParticipantId: %O)',
            this.transferState,
            this.transfereeParticipantId,
          )
        },
        { name: 'TransferStateChanged' },
      ),
    )
  }

  get maxMessageLengthReached() {
    return encodeURI(this.message).length >= 300
  }

  get selection(): PhoneNumberSelection | null {
    return this.selections[0] ?? null
  }

  get transferee(): ActiveCallParticipant | null {
    if (!this.selection || !this.app.service.user.current?.id) {
      return null
    }
    return this.call.createParticipantFromSelection(this.selection, {
      type: 'transferred',
      fromParticipantId: this.app.service.user.current.id,
    })
  }

  handleMessageChange(event: React.FormEvent<HTMLInputElement>) {
    this.message = event.currentTarget.value
  }

  handleMessageFocus() {
    this.messageFocused = true
  }

  handleMessageBlur() {
    this.messageFocused = false
  }

  handleLeaveCall() {
    this.call.leave()
  }

  handleTransfer(): Promise<void> {
    if (!this.transferee) {
      return Promise.resolve()
    }

    this.transferMode = 'transfer'
    this.transferState = 'in-progress'
    this.transfereeParticipantId = this.transferee.id

    return this.call
      .transfer(this.transferee, this.message, this.fromPhoneNumberId ?? undefined)
      .catch((error) => {
        this.reset()
        throw error
      })
  }

  /**
   * When the user goes back to the dialer, we want to reset the controller.
   */
  handleBack() {
    this.reset()
  }

  override augmentIdentityRow(row: IdentityRow) {
    row.rowMeta.delete(row.id)

    for (const phone of row.phones) {
      let numberAlreadyOnTheCall = false

      if (row instanceof AddNumberRow || row instanceof ContactRow) {
        numberAlreadyOnTheCall = this.call.participants.list.some(
          (participant) => participant.identifier === phone.number,
        )
      }
      const isInternationalNumber = !isNationalNumber(phone.number)

      const disabled = isInternationalNumber || numberAlreadyOnTheCall
      const tooltipDisabledReason = isInternationalNumber
        ? 'Support for international call transfers is coming soon'
        : numberAlreadyOnTheCall
        ? 'This number is already on the call'
        : undefined
      const labelDisabledReason = isInternationalNumber
        ? 'Support coming soon'
        : numberAlreadyOnTheCall
        ? 'Already on the call'
        : undefined

      row.phoneNumberMeta.set(phone.number, {
        disabled,
        tooltipDisabledReason,
        labelDisabledReason,
      })
    }

    const disabled = row.phones.length === 0
    if (!disabled) {
      return
    }

    const tooltipDisabledReason = `${getTypeLabel(row.type)} has no numbers`
    const labelDisabledReason = 'No numbers'
    row.rowMeta.set(row.id, {
      disabled,
      tooltipDisabledReason,
      labelDisabledReason,
    })
  }

  protected override reset() {
    this.transferMode = 'selector'
    this.transferState = null
    this.fromPhoneNumberId = null
    super.reset()
  }

  override tearDown() {
    this.reset()
    this.filteredSubscription?.unsubscribe()
    super.tearDown()
  }
}
